// PWA update controller — pure logic, no DOM. A new service worker installs and "waits"; this
// surfaces that as an "update available" state, and on the viewer's command tells it to take over
// (SKIP_WAITING) then reloads exactly once. Ported from the SkynetCore PWA playbook.

export type UpdateState = "up_to_date" | "checking" | "update_available" | "updating" | "update_failed";

export interface UpdateHandlers {
  onState?: (s: { state: UpdateState; error?: string }) => void;
  hasUnsaved?: () => boolean;
  confirmUnsaved?: () => boolean;
  reload?: () => void;
  activationTimeout?: number;
}

export interface UpdateController {
  setRegistration(reg: ServiceWorkerRegistration): void;
  available(worker: ServiceWorker | null): boolean;
  check(): Promise<boolean>;
  updateNow(): boolean;
  reloadOnce(): boolean;
  fail(error?: string): void;
}

export function createUpdateController(handlers: UpdateHandlers = {}): UpdateController {
  let state: UpdateState = "up_to_date";
  let registration: ServiceWorkerRegistration | null = null;
  let waiting: ServiceWorker | null = null;
  let reloadIssued = false;
  let activationTimer: ReturnType<typeof setTimeout> | null = null;

  const emit = (error?: string): void => handlers.onState?.(error === undefined ? { state } : { state, error });
  const setState = (next: UpdateState, error?: string): void => { state = next; emit(error); };
  const canReload = (): boolean =>
    !handlers.hasUnsaved || !handlers.hasUnsaved() || !handlers.confirmUnsaved || handlers.confirmUnsaved();

  const available = (worker: ServiceWorker | null): boolean => {
    if (!worker) return false;
    waiting = worker;
    setState("update_available");
    return true;
  };

  return {
    setRegistration(reg: ServiceWorkerRegistration): void { registration = reg; },
    available,
    async check(): Promise<boolean> {
      if (!registration) { setState("update_failed", "registration_unavailable"); return false; }
      setState("checking");
      try {
        await registration.update();
        if (registration.waiting) return available(registration.waiting);
        setState("up_to_date");
        return false;
      } catch {
        setState("update_failed", "update_check_failed");
        return false;
      }
    },
    updateNow(): boolean {
      if (!waiting || !canReload()) return false;
      setState("updating");
      try {
        waiting.postMessage({ type: "SKIP_WAITING" });
        activationTimer = setTimeout(() => {
          if (!reloadIssued) setState("update_failed", "activation_timeout");
        }, handlers.activationTimeout || 10000);
        return true;
      } catch {
        setState("update_failed", "activation_failed");
        return false;
      }
    },
    reloadOnce(): boolean {
      if (reloadIssued) return false;
      reloadIssued = true;
      if (activationTimer) clearTimeout(activationTimer);
      handlers.reload?.();
      return true;
    },
    fail(error?: string): void { setState("update_failed", error || "update_failed"); },
  };
}
